import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../stores/useAuthStore';
import useAppStore from '../../stores/useAppStore';
import useScrollPosition from '../../hooks/useScrollPosition';
import CXOEraLogo from '../shared/CXOEraLogo';

export default function Nav() {
  const scrolled = useScrollPosition(40);
  const location = useLocation();
  const progressRef = useRef(null);
  const activeSection = useAppStore(s => s.activeSection);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const goDashboard = () => {
    navigate(user?.role === 'SPEAKER' ? '/dashboard/speaker' : user?.role === 'ATTENDEE' ? '/dashboard/attendee' : user?.role === 'ADMIN' ? '/admin' : '/');
    setMenuOpen(false);
  };
  const nav = (path) => { navigate(path); setMenuOpen(false); };

  // Responsive detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900);
    check();
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Scroll progress bar
  useEffect(() => {
    const handleScroll = () => {
      if (progressRef.current) {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const width = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;
        progressRef.current.style.width = `${width}%`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getNavLinkClass = (path, relatedSections = []) => {
    if (location.pathname === path) return 'active';
    if (location.pathname === '/' && relatedSections.includes(activeSection)) return 'active';
    return '';
  };

  const showTracker = location.pathname === '/' && activeSection && activeSection !== 'home';

  const HOME_SECTIONS = [
    { id: 'home', label: 'Home' },
    { id: 'communities', label: 'Communities' },
    { id: 'upcoming', label: 'Programs' },
    { id: 'why-attend', label: 'Why Attend' },
    { id: 'testimonials', label: 'Voices' }
  ];

  return (
    <>
      <nav className={`nav ${scrolled ? 'sc' : ''}`}>
      {/* Scroll progress bar */}
      <div ref={progressRef} style={{ position: 'fixed', top: 0, left: 0, height: '2px', zIndex: 9999, pointerEvents: 'none', width: '0%', transition: 'width 0.06s linear', background: 'linear-gradient(90deg,#6B4C2A 0%,#D4845A 45%,#F2DDD2 100%)', boxShadow: '0 0 12px rgba(212,132,90,0.7), 0 0 24px rgba(212,132,90,0.3)' }} />

      {/* Logo */}
      <CXOEraLogo height={34} onClick={() => nav('/')} />

      {/* === DESKTOP CENTER: Nav links + section pill === */}
      {!isMobile && (
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Standard Page Links */}
          <ul className="nl" style={{ opacity: showTracker ? 0 : 1, transform: showTracker ? 'translateY(-10px)' : 'translateY(0)', pointerEvents: showTracker ? 'none' : 'auto', transition: 'all 0.35s ease' }}>
            <li><a href="/" className={getNavLinkClass('/', ['home', 'communities'])} onClick={e => { e.preventDefault(); nav('/'); }}>Home</a></li>
            <li><a href="/programs" className={getNavLinkClass('/programs', ['upcoming'])} onClick={e => { e.preventDefault(); nav('/programs'); }}>Programs</a></li>
            <li><a href="/about" className={getNavLinkClass('/about', ['why-attend', 'testimonials'])} onClick={e => { e.preventDefault(); nav('/about'); }}>About</a></li>
          </ul>

          {/* Section Pill Tracker (desktop only) */}
          <div style={{
            position: 'absolute', display: 'flex', alignItems: 'center', gap: '1px',
            background: 'rgba(18,16,24,0.92)', backdropFilter: 'blur(20px) saturate(1.6)',
            border: '1px solid rgba(212,132,90,0.2)', padding: '4px 6px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,132,90,0.08) inset',
            opacity: showTracker ? 1 : 0,
            pointerEvents: showTracker ? 'auto' : 'none',
            transform: showTracker ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
            transition: 'opacity 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1)'
          }}>
            {HOME_SECTIONS.map((sec, idx) => {
              const isActive = activeSection === sec.id;
              return [
                <button key={`btn-${sec.id}`} style={{
                  fontFamily: 'var(--ff-mono)', fontSize: '8px', letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: isActive ? 'var(--gold-lt)' : 'rgba(168,171,202,0.55)',
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: '7px 14px', position: 'relative',
                  borderRadius: '1px', transition: 'color 0.2s', whiteSpace: 'nowrap'
                }} onClick={() => {
                  const el = document.getElementById(sec.id);
                  if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
                }}>
                  {isActive && <div style={{ position: 'absolute', inset: 0, background: 'var(--gold)', opacity: 0.08, borderRadius: '1px' }} />}
                  <span style={{ position: 'relative', zIndex: 1 }}>{sec.label}</span>
                </button>,
                idx !== HOME_SECTIONS.length - 1 && (
                  <div key={`div-${sec.id}`} style={{ width: '1px', height: '14px', background: 'rgba(212,132,90,0.15)', flexShrink: 0 }} />
                )
              ];
            })}
          </div>
        </div>
      )}

      {/* === DESKTOP RIGHT: Auth actions === */}
      {!isMobile && (
        <div className="nav-actions">
          {user ? (
            <>
              {user.role === 'ATTENDEE' && (
                <a href="/apply-speaker" onClick={e => { e.preventDefault(); nav('/apply-speaker'); }} style={{ fontSize: 9, fontFamily: 'var(--ff-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(168,171,202,0.8)', border: '1px solid rgba(255,255,255,0.1)', padding: '8px 18px', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>Engage as Speaker?</a>
              )}
              {user.role === 'SPEAKER' && (
                <a href="/dashboard/attendee" onClick={e => { e.preventDefault(); nav('/dashboard/attendee'); }} style={{ fontSize: 9, fontFamily: 'var(--ff-mono)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(168,171,202,0.8)', border: '1px solid rgba(255,255,255,0.1)', padding: '8px 18px', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>Attend as Attendee?</a>
              )}
              <button className="ncta" onClick={goDashboard}>Dashboard</button>
              <button className="bs" style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', marginLeft: 8 }} onClick={() => { logout(); navigate('/'); }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(168,171,202,0.8)' }}>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <button className="bs" style={{ padding: '8px 18px', fontSize: 9 }} onClick={() => nav('/login')}>Sign In</button>
              <button className="ncta" onClick={() => nav('/register')}>Join CXOEra</button>
            </>
          )}
        </div>
      )}

      {/* === MOBILE: Hamburger button === */}
      {isMobile && (
        <button
          className="hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          style={{ display: 'flex', zIndex: 9002, position: 'relative' }}
        >
          <span className={`hb-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hb-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hb-line ${menuOpen ? 'open' : ''}`} />
        </button>
      )}
    </nav>

    {/* === MOBILE: Full-screen overlay menu (Moved outside <nav> to avoid clipping contexts) === */}
    {isMobile && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 8999,
        background: 'rgba(5,13,26,0.98)', backdropFilter: 'blur(24px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 0,
        opacity: menuOpen ? 1 : 0,
        pointerEvents: menuOpen ? 'auto' : 'none',
        transition: 'opacity 0.3s ease',
      }}>
          {/* Nav links */}
          <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 40 }}>
            {[
              { label: 'Home', path: '/' },
              { label: 'Programs', path: '/programs' },
              { label: 'About', path: '/about' },
            ].map((link, i) => (
              <a
                key={link.path}
                href={link.path}
                onClick={e => { e.preventDefault(); nav(link.path); }}
                style={{
                  fontSize: 28, fontFamily: "'Cormorant Garamond',serif", fontWeight: 300,
                  letterSpacing: '0.05em', color: location.pathname === link.path ? 'var(--gold)' : 'rgba(255,255,255,0.85)',
                  textDecoration: 'none', padding: '12px 0',
                  transition: 'color 0.2s',
                  transform: menuOpen ? 'translateY(0)' : 'translateY(20px)',
                  transitionDelay: `${i * 0.07}s`,
                }}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Gold divider */}
          <div style={{ width: 40, height: 1, background: 'var(--gold)', opacity: 0.4, marginBottom: 40 }} />

          {/* Auth section */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            {user ? (
              <>
                <button
                  className="ncta"
                  style={{ fontSize: 10, padding: '14px 40px', letterSpacing: '.3em' }}
                  onClick={goDashboard}
                >
                  Dashboard
                </button>
                {user.role === 'ATTENDEE' && (
                  <button className="bs" style={{ fontSize: 10, padding: '10px 32px' }} onClick={() => nav('/apply-speaker')}>
                    Engage as Speaker
                  </button>
                )}
                <button
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 12, cursor: 'pointer', letterSpacing: '.15em', textTransform: 'uppercase', marginTop: 8 }}
                  onClick={() => { logout(); navigate('/'); setMenuOpen(false); }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button className="ncta" style={{ fontSize: 10, padding: '14px 40px', letterSpacing: '.3em' }} onClick={() => nav('/register')}>
                  Join CXOEra
                </button>
                <button className="bs" style={{ fontSize: 10, padding: '10px 32px' }} onClick={() => nav('/login')}>
                  Sign In
                </button>
              </>
            )}
          </div>
      </div>
    )}
  </>
);
}
